import { useMemo, useRef, useState } from "react"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Trash2, Pencil, Upload } from "lucide-react"
import clsx from "clsx"
import { api, ApiError, type CatalogResponse, type Tag, type VideoSummary } from "@/lib/types"
import { GlarchiveSubNav } from "@/components/GlarchiveSubNav"
import { Drawer } from "@lilbillbiscuit/account-ui"
import { TagChip } from "@lilbillbiscuit/account-ui"
import { formatDate, formatRelative } from "@/lib/format"

type FormState = {
  slug: string
  title: string
  description: string
  sourceType: "r2" | "youtube" | "gdrive" | "onedrive"
  sourceRef: string
  thumbnailUrl: string
  performedOn: string
  durationSeconds: string
  status: "draft" | "published" | "archived"
  tagSlugs: string[]
  uploadFile: File | null
}

const EMPTY_FORM: FormState = {
  slug: "", title: "", description: "", sourceType: "youtube", sourceRef: "",
  thumbnailUrl: "", performedOn: "", durationSeconds: "", status: "draft",
  tagSlugs: [], uploadFile: null,
}

export function AdminVideos() {
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "published" | "archived">("all")
  const [editing, setEditing] = useState<VideoSummary | "new" | null>(null)

  const tags = useQuery<{ tags: Tag[] }>({ queryKey: ["adminTagsAll"], queryFn: () => api("/api/admin/tags") })
  const videos = useInfiniteQuery<CatalogResponse>({
    queryKey: ["adminVideos", q, statusFilter],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api<CatalogResponse>("/api/admin/videos", {
      query: { q: q || undefined, status: statusFilter === "all" ? undefined : statusFilter, cursor: (pageParam ?? undefined) as string | undefined, limit: 50 },
    }),
    getNextPageParam: (last) => last.nextCursor,
  })

  const archive = useMutation<unknown, ApiError, string>({
    mutationFn: (id) => api(`/api/admin/videos/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminVideos"] }),
  })

  const items: VideoSummary[] = videos.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="space-y-6">
      <GlarchiveSubNav />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold">Videos</h1>
        <div className="flex items-center gap-2">
          <input className="input w-56" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div role="radiogroup" aria-label="Status filter" className="flex rounded-lg border border-th-glass/[0.12] bg-th-glass/[0.04] p-0.5 text-sm">
            {(["all", "draft", "published", "archived"] as const).map((s) => (
              <button
                key={s}
                role="radio"
                aria-checked={statusFilter === s}
                onClick={() => setStatusFilter(s)}
                className={clsx(
                  "rounded-md px-2 py-1 text-xs transition-colors",
                  statusFilter === s ? "bg-th-glass/[0.1] text-th-fg" : "text-th-fg/50 hover:text-th-fg"
                )}
              >{s}</button>
            ))}
          </div>
          <button className="btn-primary" onClick={() => setEditing("new")}>
            <Plus size={14} /> Add video
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-th-glass/[0.08]">
        <table className="w-full text-sm">
          <thead className="bg-th-glass/[0.04] text-left text-xs uppercase tracking-wide text-th-fg/50">
            <tr>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2 w-24">Source</th>
              <th className="px-4 py-2 w-32">Performed</th>
              <th className="px-4 py-2">Tags</th>
              <th className="px-4 py-2 w-28">Status</th>
              <th className="px-4 py-2 w-32">Updated</th>
              <th className="px-4 py-2 w-20 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className="border-t border-th-glass/[0.06] hover:bg-th-glass/[0.04]">
                <td className="px-4 py-2">
                  <div className="font-medium text-th-fg">{v.title}</div>
                  <div className="text-xs text-th-fg/40 font-mono">{v.slug}</div>
                </td>
                <td className="px-4 py-2 text-xs text-th-fg/60">{v.source_type}</td>
                <td className="px-4 py-2 text-th-fg/60">{formatDate(v.performed_on)}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {v.tag_slugs.slice(0, 4).map((s) => <TagChip key={s} slug={s} />)}
                    {v.tag_slugs.length > 4 && <span className="tag-badge">+{v.tag_slugs.length - 4}</span>}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <StatusBadge status={v.status} />
                </td>
                <td className="px-4 py-2 text-th-fg/60">{formatRelative(v.updated_at_ms)}</td>
                <td className="px-4 py-2 text-right">
                  <button className="btn-ghost" onClick={() => setEditing(v)} aria-label="Edit"><Pencil size={13} /></button>
                  {v.status !== "archived" && (
                    <button
                      className="btn-ghost text-red-400 hover:text-red-300"
                      onClick={() => confirm(`Archive "${v.title}"?`) && archive.mutate(v.id)}
                      aria-label="Archive"
                    ><Trash2 size={13} /></button>
                  )}
                </td>
              </tr>
            ))}
            {!videos.isLoading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-th-fg/40">No videos yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {videos.hasNextPage && (
        <div className="pt-2 text-center">
          <button className="btn" onClick={() => videos.fetchNextPage()} disabled={videos.isFetchingNextPage}>
            {videos.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      <Drawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "Add video" : "Edit video"}
      >
        {editing !== null && (
          <VideoForm
            initial={editing === "new" ? null : editing}
            allTags={tags.data?.tags ?? []}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ["adminVideos"] })
              setEditing(null)
            }}
          />
        )}
      </Drawer>
    </div>
  )
}

function StatusBadge({ status }: { status: VideoSummary["status"] }) {
  const cls = status === "published"
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
    : status === "draft"
    ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
    : "border-th-glass/[0.12] bg-th-glass/[0.04] text-th-fg/50"
  return <span className={clsx("tag-badge", cls)}>{status}</span>
}

function VideoForm({ initial, allTags, onSaved }: { initial: VideoSummary | null; allTags: Tag[]; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(() =>
    initial
      ? {
          slug: initial.slug, title: initial.title, description: initial.description ?? "",
          sourceType: initial.source_type, sourceRef: initial.source_type === "r2" ? "" : initial.source_ref,
          thumbnailUrl: initial.thumbnail_url ?? "", performedOn: initial.performed_on ?? "",
          durationSeconds: initial.duration_seconds?.toString() ?? "",
          status: initial.status, tagSlugs: [...initial.tag_slugs], uploadFile: null,
        }
      : EMPTY_FORM
  )
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Synchronous re-entry guard — React state updates lag a tick so a fast
  // double-submit can otherwise sneak through before busy=true is rendered.
  const submittingRef = useRef(false)

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (submittingRef.current) return
    submittingRef.current = true
    setError(null)
    setBusy(true)
    try {
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        sourceType: form.sourceType,
        sourceRef: form.sourceType === "r2" ? "" : form.sourceRef.trim(),
        thumbnailUrl: form.thumbnailUrl.trim() || null,
        performedOn: form.performedOn || null,
        durationSeconds: form.durationSeconds ? Number(form.durationSeconds) : null,
        status: form.status,
      }
      if (!initial) {
        payload.slug = form.slug.trim()
        payload.tagSlugs = form.tagSlugs
        // Force draft for r2 sources with a pending upload — we don't want a
        // published row pointing at a missing R2 object if the upload fails.
        if (form.sourceType === "r2" && form.uploadFile) payload.status = "draft"
        const created = await api<{ video: { id: string }; uploadUrl: string | null }>("/api/admin/videos", {
          method: "POST",
          json: payload,
        })
        if (form.sourceType === "r2" && form.uploadFile && created.uploadUrl) {
          try {
            await uploadFile(created.uploadUrl, form.uploadFile)
            // If the user asked for "published", flip after a successful upload.
            if (form.status === "published") {
              await api(`/api/admin/videos/${created.video.id}`, {
                method: "PATCH",
                json: { status: "published" },
              })
            }
          } catch (upErr) {
            // Roll back the newly created row so we don't leak an orphan.
            try { await api(`/api/admin/videos/${created.video.id}`, { method: "DELETE" }) } catch {}
            throw upErr
          }
        }
      } else {
        const diff = diffTags(initial.tag_slugs, form.tagSlugs)
        const patch: Record<string, unknown> = { ...payload, addTags: diff.add, removeTags: diff.remove }
        await api(`/api/admin/videos/${initial.id}`, { method: "PATCH", json: patch })
        if (form.sourceType === "r2" && form.uploadFile) {
          await uploadFile(`/api/admin/videos/${initial.id}/asset`, form.uploadFile)
        }
      }
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage ?? `Error ${err.status}` : String(err))
    } finally {
      submittingRef.current = false
      setBusy(false)
    }
  }

  const fileTooLarge = useMemo(() => {
    if (!form.uploadFile) return false
    return form.uploadFile.size > 100 * 1024 * 1024
  }, [form.uploadFile])

  return (
    <form onSubmit={submit} className="space-y-4">
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Slug</div>
        <input className="input w-full font-mono" value={form.slug} onChange={(e) => set("slug", e.target.value)} disabled={!!initial} required />
      </label>
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Title</div>
        <input className="input w-full" value={form.title} onChange={(e) => set("title", e.target.value)} required />
      </label>
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Description</div>
        <textarea className="input w-full min-h-[80px]" value={form.description} onChange={(e) => set("description", e.target.value)} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="mb-1 text-xs text-th-fg/60">Performed on</div>
          <input type="date" className="input w-full" value={form.performedOn} onChange={(e) => set("performedOn", e.target.value)} />
        </label>
        <label className="block">
          <div className="mb-1 text-xs text-th-fg/60">Duration (sec)</div>
          <input type="number" min="0" className="input w-full" value={form.durationSeconds} onChange={(e) => set("durationSeconds", e.target.value)} />
        </label>
      </div>
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Source</div>
        <select className="input w-full" value={form.sourceType} onChange={(e) => set("sourceType", e.target.value as FormState["sourceType"])}>
          <option value="youtube">YouTube</option>
          <option value="gdrive">Google Drive</option>
          <option value="onedrive">OneDrive / SharePoint</option>
          <option value="r2">Self-hosted (R2)</option>
        </select>
      </label>
      {form.sourceType !== "r2" ? (
        <label className="block">
          <div className="mb-1 text-xs text-th-fg/60">
            {form.sourceType === "onedrive" ? "Embed iframe OR share URL" : "Source URL or ID"}
          </div>
          {form.sourceType === "onedrive" ? (
            <>
              <textarea
                className="input w-full min-h-[88px] font-mono text-xs"
                value={form.sourceRef}
                onChange={(e) => set("sourceRef", e.target.value)}
                required={!initial}
                placeholder='&lt;iframe src="https://…sharepoint.com/…/embed.aspx?UniqueId=…" …&gt;&lt;/iframe&gt;'
              />
              <div className="mt-1 text-[11px] text-th-fg/50">
                <strong className="text-th-fg/70">Preferred:</strong> in OneDrive web, open the video → toolbar → <em>Embed</em> → copy the iframe and paste it here. UniqueId-based embed URLs survive file renames.
                <br />
                <strong className="text-th-fg/70">Or:</strong> paste a plain share URL (must be set to "anyone with the link") and we'll resolve it server-side.
              </div>
            </>
          ) : (
            <input
              className="input w-full"
              value={form.sourceRef}
              onChange={(e) => set("sourceRef", e.target.value)}
              required={!initial}
              placeholder={
                form.sourceType === "youtube" ? "https://www.youtube.com/watch?v=…"
                : "https://drive.google.com/file/d/…/view"
              }
            />
          )}
        </label>
      ) : (
        <div>
          <div className="mb-1 text-xs text-th-fg/60 flex items-center gap-1">
            <Upload size={12} /> Upload file
          </div>
          <input
            type="file"
            accept="video/*"
            className="block w-full text-sm text-th-fg/70 file:mr-3 file:rounded-lg file:border file:border-th-glass/[0.12] file:bg-th-glass/[0.04] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-th-fg/85 hover:file:bg-th-glass/[0.08]"
            onChange={(e) => set("uploadFile", e.target.files?.[0] ?? null)}
          />
          {fileTooLarge && (
            <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-400">
              Files over 100 MB can't go through this form (Cloudflare Worker body limit). Use <code className="font-mono">wrangler r2 object put</code> from CLI instead.
            </div>
          )}
        </div>
      )}
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Thumbnail URL (https)</div>
        <input className="input w-full" value={form.thumbnailUrl} onChange={(e) => set("thumbnailUrl", e.target.value)} placeholder="https://…" />
      </label>
      <div>
        <div className="mb-1 text-xs text-th-fg/60">Tags</div>
        <div className="flex flex-wrap gap-1">
          {allTags.map((t) => (
            <TagChip key={t.slug} slug={t.slug} active={form.tagSlugs.includes(t.slug)} onClick={() =>
              set("tagSlugs", form.tagSlugs.includes(t.slug) ? form.tagSlugs.filter((s) => s !== t.slug) : [...form.tagSlugs, t.slug])
            } />
          ))}
        </div>
      </div>
      <label className="block">
        <div className="mb-1 text-xs text-th-fg/60">Status</div>
        <select className="input w-full" value={form.status} onChange={(e) => set("status", e.target.value as FormState["status"])}>
          <option value="draft">draft</option>
          <option value="published">published</option>
          <option value="archived">archived</option>
        </select>
      </label>
      {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-sm text-red-400">{error}</div>}
      <button type="submit" className="btn-primary" disabled={busy || fileTooLarge}>
        {busy ? "Saving…" : initial ? "Save changes" : "Create video"}
      </button>
    </form>
  )
}

function diffTags(prev: string[], next: string[]) {
  const prevSet = new Set(prev)
  const nextSet = new Set(next)
  return {
    add: next.filter((s) => !prevSet.has(s)),
    remove: prev.filter((s) => !nextSet.has(s)),
  }
}

async function uploadFile(url: string, file: File): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "content-type": file.type || "application/octet-stream" },
    credentials: "include",
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Upload failed (${res.status}): ${text}`)
  }
}
